import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarDays, Copy, CopyPlus, Eye, EyeOff, Pencil, Plus, Trash2, Users } from "lucide-react";
import { AdminShell } from "../components/admin/AdminShell";
import { API, fmtEventDate, fmtEventTime, fmtPrice } from "../lib/eventUtils";
import { EventForm } from "../components/admin/EventForm";
import { RosterPanel } from "../components/admin/RosterPanel";

export default function AdminEvents() {
  const [events, setEvents] = useState(null);
  const [editing, setEditing] = useState(null);
  const [rosterFor, setRosterFor] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [error, setError] = useState("");

  const loadEvents = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/events`, { withCredentials: true });
      setEvents(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not load events");
    }
  };

  useEffect(() => {
    loadEvents();
    axios
      .get(`${API}/api/admin/settings`, { withCredentials: true })
      .then((r) => setDefaults({ capacity: r.data.default_event_capacity, partner: r.data.default_partner }))
      .catch(() => {});
  }, []);

  const patch = async (ev, data, okMsg) => {
    try {
      await axios.patch(`${API}/api/admin/events/${ev.event_id}`, data, { withCredentials: true });
      toast.success(okMsg);
      loadEvents();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const copyLink = (ev) => {
    navigator.clipboard.writeText(`${window.location.origin}/events/${ev.slug}`);
    toast.success("Public link copied");
  };

  const duplicate = async (ev) => {
    try {
      const { data } = await axios.post(`${API}/api/admin/events/${ev.event_id}/duplicate`, {}, { withCredentials: true });
      toast.success(`Duplicated as "${data.title}" (draft)`);
      loadEvents();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Duplicate failed");
    }
  };

  const removeEvent = async (ev) => {
    if (!window.confirm(
      `Delete "${ev.title}"?\n\nThis permanently removes the event, its ${ev.registered_count} registration(s) and its waitlist. This cannot be undone.`
    )) return;
    try {
      const { data } = await axios.delete(`${API}/api/admin/events/${ev.event_id}`, { withCredentials: true });
      toast.success(`Event deleted (${data.registrations_removed} registrations, ${data.waitlist_removed} waitlist entries removed)`);
      loadEvents();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <AdminShell title="Events">
      <div data-testid="admin-events-page">
        <div className="flex justify-end mb-6">
          <button onClick={() => { setEditing("new"); setRosterFor(null); }} className="btn-volt !py-2.5 !px-5 !text-sm" data-testid="admin-new-event-button">
            <Plus size={15} /> New Event
          </button>
        </div>

        {editing && (
          <EventForm
            event={editing === "new" ? null : editing}
            defaults={defaults}
            onSaved={() => { setEditing(null); loadEvents(); }}
            onCancel={() => setEditing(null)}
          />
        )}
        {rosterFor && (
          <RosterPanel event={rosterFor} onClose={() => setRosterFor(null)} onChanged={loadEvents} />
        )}

        {error && <p className="border border-red-500/40 bg-red-500/10 text-red-300 text-sm px-4 py-3 mb-6" data-testid="admin-events-error">{error}</p>}
        {events === null && !error && <p className="font-mono2 text-xs uppercase tracking-[0.25em] text-zinc-500 animate-pulse" data-testid="admin-events-loading">Loading…</p>}

        {events !== null && (
          <div className="space-y-px bg-white/10 border border-white/10">
            {events.length === 0 && (
              <p className="bg-[#0c0c0c] p-8 text-zinc-500 text-sm" data-testid="admin-no-events">No events yet — create your first one.</p>
            )}
            {events.map((ev) => (
              <article key={ev.event_id} className="bg-[#0c0c0c] p-6 sm:p-7" data-testid={`admin-event-row-${ev.slug}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="font-mono2 text-[9px] uppercase tracking-[0.2em] border border-white/20 text-zinc-400 px-2 py-0.5">{ev.partner}</span>
                      <span className={`font-mono2 text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 border ${ev.published ? "border-volt/60 text-volt" : "border-white/20 text-zinc-500"}`} data-testid={`admin-published-badge-${ev.slug}`}>
                        {ev.published ? "Published" : "Draft"}
                      </span>
                      <span className={`font-mono2 text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 border ${ev.registration_open ? "border-emerald-500/60 text-emerald-400" : "border-white/20 text-zinc-500"}`} data-testid={`admin-regopen-badge-${ev.slug}`}>
                        {ev.registration_open ? "Registration Open" : "Registration Closed"}
                      </span>
                      {ev.price_cents > 0 && (
                        <span className="font-mono2 text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 border border-volt/60 text-volt" data-testid={`admin-price-badge-${ev.slug}`}>
                          {fmtPrice(ev.price_cents)}
                        </span>
                      )}
                    </div>
                    <h2 className="font-head font-extrabold uppercase text-2xl tracking-tight mb-1">{ev.title}</h2>
                    <p className="font-mono2 text-[11px] uppercase tracking-[0.15em] text-zinc-500 flex flex-wrap gap-x-5 gap-y-1">
                      <span className="flex items-center gap-1.5"><CalendarDays size={12} /> {fmtEventDate(ev.date_start)} · {fmtEventTime(ev.date_start)}</span>
                      <span className="flex items-center gap-1.5"><Users size={12} /> {ev.registered_count}/{ev.capacity} registered · {ev.spots_remaining} left</span>
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => { setEditing(ev); setRosterFor(null); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="btn-ghost !py-2 !px-3.5 !text-xs" data-testid={`admin-edit-${ev.slug}`}>
                      <Pencil size={13} /> Edit
                    </button>
                    <button onClick={() => duplicate(ev)} className="btn-ghost !py-2 !px-3.5 !text-xs" data-testid={`admin-duplicate-${ev.slug}`}>
                      <CopyPlus size={13} /> Duplicate
                    </button>
                    <button onClick={() => patch(ev, { published: !ev.published }, ev.published ? "Event unpublished" : "Event published")} className="btn-ghost !py-2 !px-3.5 !text-xs" data-testid={`admin-publish-toggle-${ev.slug}`}>
                      {ev.published ? <><EyeOff size={13} /> Unpublish</> : <><Eye size={13} /> Publish</>}
                    </button>
                    <button onClick={() => patch(ev, { registration_open: !ev.registration_open }, ev.registration_open ? "Registration closed" : "Registration opened")} className="btn-ghost !py-2 !px-3.5 !text-xs" data-testid={`admin-reg-toggle-${ev.slug}`}>
                      {ev.registration_open ? "Close Reg" : "Open Reg"}
                    </button>
                    <button onClick={() => copyLink(ev)} className="btn-ghost !py-2 !px-3.5 !text-xs" data-testid={`admin-copy-link-${ev.slug}`}>
                      <Copy size={13} /> Copy Link
                    </button>
                    <button onClick={() => { setRosterFor(ev); setEditing(null); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="btn-volt !py-2 !px-3.5 !text-xs" data-testid={`admin-roster-${ev.slug}`}>
                      <Users size={13} /> Roster
                    </button>
                    <button onClick={() => removeEvent(ev)} className="btn-ghost !py-2 !px-3 !text-xs !border-red-500/30 hover:!border-red-500/60 hover:!text-red-400" data-testid={`admin-delete-${ev.slug}`} title="Delete event">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </AdminShell>
  );
}
